import React, { useState } from 'react';
import './index.css';
import DriverList from './DriverList';
import PickupList from './PickupList';
import moment from 'moment';
import ScheduleCalendar from './ScheduleCalendar';
import toast from 'react-hot-toast';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { handleApiRequest } from '../../../utils/apiHandler';
import api from '../../../services/axios';

const SchedulePickups = () => {
  const [pickups, setPickups] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(moment());
  const [confirmData, setConfirmData] = useState(null);

  const [activeDragItem, setActiveDragItem] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),

    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
  );

  const getFreshPickup = async (pickupId) => {
    const result = await handleApiRequest({
      requestFn: () => api.get(`/manager/pickups/${pickupId}`),
    });

    if (!result?.success) {
      return null;
    }
    return result.data.data;
  };

  const assignDriverToPickup = async ({
    driver,
    pickup,
    skipConfirm = false,
  }) => {
    const pickupTruckType = pickup?.truck_type?.id;

    const driverTruckTypes = driver?.truck_types?.map((t) => t.id) || [];

    const isMatch = driverTruckTypes.includes(pickupTruckType);

    if (!isMatch) {
      toast.error("Impossible to assign, truck types don't match");

      return;
    }

    const freshPickup = await getFreshPickup(pickup.id);

    if (!freshPickup) {
      toast.error('Failed to load delivery.');

      return;
    }

    if (freshPickup.driver && !skipConfirm) {
      setConfirmData({
        driver,
        pickup: freshPickup,
      });

      return;
    }

    if (freshPickup.driver) {
      const unclaimResult = await handleApiRequest({
        requestFn: () => api.post(`/manager/pickups/${pickup.id}/unclaim`),

        onError: (message) => {
          toast.error(message || 'Failed to unassign driver');
        },
      });

      if (!unclaimResult) {
        return;
      }
    }

    if (!freshPickup.waiting_for_driver_at) {
      toast.error("This delivery cannot be claimed yet because it hasn't been paid.");

      return;
    }

    await handleApiRequest({
      requestFn: () =>
        api.post(`/manager/pickups/${pickup.id}/claim`, {
          driver_id: `${driver.id}`,
        }),
      onSuccess: () => {
        setPickups((prev) =>
          prev.map((p) =>
            p.id === pickup.id
              ? {
                  ...p,
                  driver,
                  driver_id: driver.id,
                }
              : p,
          ),
        );

        toast.success('Driver has been successfully assigned to the delivery');
      },
      onError: (error) => {
        toast.error(error || 'Failed to assign driver');
      },
    });
  };

  const schedulePickup = async ({ pickupId, date, hour }) => {
    const scheduledTime = moment(date)
      .hour(hour)
      .minute(0)
      .second(0)
      .format('YYYY-MM-DDTHH:mm');

    await handleApiRequest({
      requestFn: () =>
        api.post(`/manager/pickups/${pickupId}/update`, {
          scheduled_pickup_time: scheduledTime,
        }),
      onSuccess: ({ pickup: updatedPickup }) => {
        console.log(updatedPickup);
        setPickups((prev) =>
          prev.map((pickup) =>
            pickup.id === updatedPickup.id ? updatedPickup : pickup,
          ),
        );

        toast.success(
          `Delivery has been successfully scheduled on ${moment(scheduledTime).format(
            'dddd h:mm A',
          )}`,
        );
      },
      onError: (error) => {
        toast.error(error || 'Failed to schedule delivery');
      },
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={(event) => {
        setActiveDragItem(event.active.data.current);
      }}
      onDragEnd={(event) => {
        const { active, over } = event;

        setActiveDragItem(null);

        if (!over) return;

        const activeData = active.data.current;
        const overData = over.data.current;

        // DRIVER -> PICKUP
        if (activeData?.driver && overData?.pickup) {
          assignDriverToPickup({
            driver: activeData.driver,
            pickup: overData.pickup,
          });
        }

        // PICKUP -> CALENDAR SLOT
        if (activeData?.pickup && overData?.day) {
          schedulePickup({
            pickupId: activeData.pickup.id,
            date: overData.day,
            hour: overData.hour,
          });
        }
      }}
    >
      <div className="px-4 py-0 h-100">
        <h2 className="text-center fw-bold mb-4">Schedule Deliveries</h2>

        <div className="row g-3">
          <div className="col-12 col-md-6 col-xl-3 p-1">
            <DriverList drivers={drivers} setDrivers={setDrivers} />
          </div>
          <div className="col-12 col-md-6 col-xl-3 p-1">
            <PickupList
              pickups={pickups}
              setPickups={setPickups}
              assignDriverToPickup={assignDriverToPickup}
            />
          </div>
          <div className="col-12 col-xl-6">
            <ScheduleCalendar
              currentWeek={currentWeek}
              setCurrentWeek={setCurrentWeek}
              pickups={pickups}
              schedulePickup={schedulePickup}
              assignDriverToPickup={assignDriverToPickup}
            />
          </div>
        </div>

        {confirmData && (
          <div className="confirm-overlay">
            <div className="confirm-modal">
              <h5 className="fw-bold mb-3">
                Please confirm that previus driver will be changed to new one
              </h5>

              <div className="d-flex gap-2 justify-content-end">
                <button
                  className="btn btn-light"
                  onClick={() => setConfirmData(null)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-primary"
                  onClick={() => {
                    assignDriverToPickup({
                      driver: confirmData.driver,
                      pickup: confirmData.pickup,
                      skipConfirm: true,
                    });

                    setConfirmData(null);
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <DragOverlay>
        {activeDragItem?.driver && (
          <div
            className="card border-0 driver-card"
            style={{
              width: '376px',
              transform: 'rotate(2deg)',
              opacity: 0.95,
              boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
              borderRadius: '16px',
              pointerEvents: 'none',
            }}
          >
            <div className="card-body">
              <div className="fw-bold">{activeDragItem.driver.name}</div>

              <div className="text-muted small">Driver</div>
            </div>
          </div>
        )}

        {activeDragItem?.pickup && (
          <div
            className="card border-0 driver-card"
            style={{
              width: '376px',
              transform: 'rotate(2deg)',
              opacity: 0.95,
              boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
              borderRadius: '16px',
              pointerEvents: 'none',
            }}
          >
            <div className="card-body">
              <div className="fw-bold">Delivery #{activeDragItem.pickup.id}</div>
            </div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default SchedulePickups;
